import { manufacturingFetch } from "@/lib/manufacturingClient";

export type MaterialUsageLine = {
  spoken_phrase: string;
  quantity: number;
  id?: string;
  match_status: "matched" | "ambiguous" | "unmatched" | "overridden" | "rematched";
  material_id: string | null;
  material_name: string | null;
  unit: string | null;
  candidates: { id: string; name: string; unit: string }[];
};

export type MaterialUsagePreview = {
  serial_text: string;
  mfg_unit_id: string | null;
  serial_found: boolean;
  plant_id: string | null;
  plant_name: string | null;
  plant_status: "matched" | "ambiguous" | "unmatched";
  plant_candidates: { id: string; name: string }[];
  plants: { id: string; name: string }[];
  lines: MaterialUsageLine[];
  catalog: { id: string; name: string; unit: string; code: string }[];
};

export type MaterialUsageDraftSummary = {
  id: string;
  plant_id: string | null;
  plant_name: string | null;
  serial_text: string;
  status: string;
  updated_at: string;
  line_count: number;
};

export function extractMaterialUsage(body: {
  transcript?: string;
  plant_id?: string;
  serial_text?: string;
  lines?: { quantity: number; spoken_phrase: string }[];
}) {
  return manufacturingFetch<{ ok: true; preview: MaterialUsagePreview }>("/api/service/material-usage/extract", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function listMaterialUsageDrafts() {
  return manufacturingFetch<{ ok: true; reports: MaterialUsageDraftSummary[] }>("/api/service/material-usage/drafts", {
    method: "GET",
  });
}

export function getMaterialUsageDraft(id: string) {
  return manufacturingFetch<{
    ok: true;
    report: {
      id: string;
      plant_id: string | null;
      serial_text: string;
      mfg_unit_id: string | null;
      transcript: string | null;
      status: string;
      corrects_report_id: string | null;
      has_file: boolean;
      mail_status: string;
      catalog: { id: string; name: string; unit: string; code: string }[];
      lines: {
        id: string;
        spoken_phrase: string;
        quantity: number;
        material_id: string | null;
        material_name: string | null;
        unit: string | null;
        match_status: MaterialUsageLine["match_status"];
      }[];
    };
  }>(`/api/service/material-usage/drafts/get?id=${encodeURIComponent(id)}`, { method: "GET" });
}

export function saveMaterialUsageDraft(body: {
  id?: string;
  plant_id: string | null;
  serial_text: string;
  transcript: string | null;
  lines: {
    spoken_phrase: string;
    quantity: number;
    material_id: string | null;
    match_status: MaterialUsageLine["match_status"];
  }[];
}) {
  return manufacturingFetch<{ ok: true; id: string }>("/api/service/material-usage/drafts/save", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export type MaterialUsageReportStatus = "draft" | "certified" | "submitted";

export function certifyMaterialUsage(id: string) {
  return manufacturingFetch<{ ok: true; certified_name: string; certified_at: string }>(
    "/api/service/material-usage/certify",
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id }) },
  );
}

export function reopenMaterialUsage(id: string) {
  return manufacturingFetch<{ ok: true }>("/api/service/material-usage/reopen", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id }),
  });
}

export function submitMaterialUsage(id: string) {
  return manufacturingFetch<{ ok: true; mail_status: string }>("/api/service/material-usage/submit", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id }),
  });
}

export type NegativeMaterial = {
  id: string;
  name: string;
  material_code: string;
  on_hand_qty: number;
  plant_name: string;
};

export function rematchMaterialUsageLine(body: { report_id: string; line_id: string; material_id: string | null }) {
  return manufacturingFetch<{ ok: true }>("/api/service/material-usage/rematch", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function startMaterialUsageCorrection(id: string) {
  return manufacturingFetch<{ ok: true; id: string }>("/api/service/material-usage/correct", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id }),
  });
}

export function saveMaterialUsageCorrection(body: { id: string; lines: { id: string; quantity: number }[] }) {
  return manufacturingFetch<{ ok: true }>("/api/service/material-usage/correction/save", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function openMaterialUsageFile(id: string) {
  return manufacturingFetch<{ ok: true; url: string }>(
    `/api/service/material-usage/file?id=${encodeURIComponent(id)}`,
    { method: "GET" },
  );
}

export function resendMaterialUsage(id: string) {
  return manufacturingFetch<{ ok: true; mail_status: string }>("/api/service/material-usage/resend", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id }),
  });
}

export function listNegativeMaterials() {
  return manufacturingFetch<{ ok: true; materials: NegativeMaterial[] }>("/api/service/material-usage/negative-stock", {
    method: "GET",
  });
}
